/**
 * AdminAIUsageTab Component
 *
 * OpenRouter AI usage and cost analytics dashboard for the admin panel.
 * Shows cost breakdowns by service, model, user, conversation, and organization.
 * Supports triggering cost verification against OpenRouter's generation API.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { getAIUsageClient } from '@/components/admin/adminAuthClients';
import {
  Activity,
  RefreshCw,
  DollarSign,
  Zap,
  Clock,
  CheckCircle2,
  BarChart3,
  Users,
  MessageSquare,
  Server,
  Brain,
  TrendingUp,
  AlertCircle,
  Loader2,
  Wallet,
  ChevronDown,
  ChevronRight,
  XCircle,
  Bot,
} from 'lucide-react';
import * as errorUtils from '@/utils/errors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CostGroup {
  groupBy: Record<string, string>;
  estimatedCost: number;
  actualCost: number;
  verifiedCount: number;
  totalRequests: number;
  totalTokens: number;
  avgResponseTime: number;
  successRate: number;
}

interface CostResponse {
  success: boolean;
  data: CostGroup[];
  meta: {
    groupBy: string[];
    days: number;
    windowMinutes?: number;
    verifiedOnly: boolean;
    totalGroups: number;
    timestamp: string;
  };
}

interface DashboardResponse {
  success: boolean;
  data: {
    summary: {
      totalGenerations: number;
      successRate: number;
      totalTokens: number;
      totalCost: number;
      cacheHitRate: number;
      avgResponseTime: number;
    };
    featureUsage: Array<{ _id: string; count: number; cost: number; tokens: number }>;
    timeline: Array<{ _id: string; count: number; cost: number; tokens: number }>;
    recentActivity: Array<{
      id: string;
      type: string;
      timestamp: string;
      success: boolean;
      tokens: { prompt: number; completion: number; total: number };
      cost: number;
      cached: boolean;
      responseTime: number;
      provider: string;
    }>;
  };
}

interface VerifyResult {
  processed: number;
  verified: number;
  failed: number;
}

interface OpenRouterBalance {
  limit: number | null;
  limitRemaining: number | null;
  limitReset: string | null;
  usage: number;
  usageDaily: number;
  usageWeekly: number;
  usageMonthly: number;
  isFreeTier: boolean;
  totalCredits: number | null;
  totalUsage: number | null;
  accountBalance: number | null;
}

interface AxiosLikeError {
  response?: { status?: number; data?: { error?: string; message?: string; code?: string } };
  request?: unknown;
  message?: string;
}

function isAxiosLikeError(value: unknown): value is AxiosLikeError {
  return typeof value === 'object' && value !== null;
}

interface TraceRecord {
  _id: string;
  requestType: string;
  timestamp: string;
  success: boolean;
  tokens: { prompt: number; completion: number; total: number };
  cost: number;
  actualCost?: number | null;
  costVerified?: boolean;
  cached: boolean;
  responseTime: number;
  provider: string;
  vendor?: string | null;
  aiModel: string;
  service: string | null;
  agentType: string | null;
  errorType: string | null;
  generationId?: string | null;
}

interface RecentConversation {
  conversationId: string;
  firstSeen: string;
  lastSeen: string;
  callCount: number;
  totalCost: number;
}

type GroupByOption = 'service' | 'model' | 'user' | 'provider' | 'vendor' | 'requestType' | 'organization' | 'conversation' | 'agentType';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return `$${cost.toFixed(6)}`;
  if (cost < 1) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return tokens.toString();
}

function formatMs(ms: number): string {
  if (!ms) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function groupByLabel(key: string, value: string | null): string {
  if (!value || value === 'null') return 'Unknown';
  return value;
}

function serializeHistoryFilters(filters: Record<string, string>): string {
  return new URLSearchParams(
    Object.entries(filters).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)),
  ).toString();
}

const GROUP_BY_OPTIONS: { value: GroupByOption; label: string; icon: React.ReactNode }[] = [
  { value: 'service', label: 'Service', icon: <Server className="h-4 w-4" /> },
  { value: 'model', label: 'Model', icon: <Brain className="h-4 w-4" /> },
  { value: 'vendor', label: 'Vendor', icon: <Zap className="h-4 w-4" /> },
  { value: 'provider', label: 'Provider', icon: <Zap className="h-4 w-4" /> },
  { value: 'user', label: 'User', icon: <Users className="h-4 w-4" /> },
  { value: 'requestType', label: 'Request Type', icon: <Activity className="h-4 w-4" /> },
  { value: 'organization', label: 'Organization', icon: <BarChart3 className="h-4 w-4" /> },
  { value: 'conversation', label: 'Conversation', icon: <MessageSquare className="h-4 w-4" /> },
  { value: 'agentType', label: 'Agent', icon: <Bot className="h-4 w-4" /> },
];

// Period options: label + lookback in minutes. Supports sub-day windows for
// debug-loop iteration; long windows for billing-style aggregates.
const PERIOD_OPTIONS: { label: string; minutes: number }[] = [
  { label: '30 min', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '3 hours', minutes: 3 * 60 },
  { label: '6 hours', minutes: 6 * 60 },
  { label: '12 hours', minutes: 12 * 60 },
  { label: '1 day', minutes: 24 * 60 },
  { label: '7 days', minutes: 7 * 24 * 60 },
  { label: '14 days', minutes: 14 * 24 * 60 },
  { label: '30 days', minutes: 30 * 24 * 60 },
  { label: '60 days', minutes: 60 * 24 * 60 },
  { label: '90 days', minutes: 90 * 24 * 60 },
  { label: '180 days', minutes: 180 * 24 * 60 },
  { label: '365 days', minutes: 365 * 24 * 60 },
];

function periodLabel(minutes: number): string {
  const found = PERIOD_OPTIONS.find((o) => o.minutes === minutes);
  if (found) return found.label;
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)} hr`;
  return `${Math.round(minutes / (24 * 60))} days`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SummaryCard: React.FC<{
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}> = ({ label, value, subtitle, icon, color = 'text-primary' }) => (
  <div className="border rounded-lg p-4 bg-card">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <span className={`${color}`}>{icon}</span>
    </div>
    <div className="text-2xl font-bold">{value}</div>
    {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
  </div>
);

const ConversationLookup: React.FC = () => {
  const [inputId, setInputId] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentConversation[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);

  useEffect(() => {
    const loadRecent = async () => {
      setRecentLoading(true);
      try {
        const client = getAIUsageClient();
        const { data } = await client.get<{ success: boolean; data: RecentConversation[] }>(
          '/conversations/recent?days=1&limit=20',
        );
        setRecent(data.data || []);
      } catch {
        setRecent([]);
      } finally {
        setRecentLoading(false);
      }
    };
    loadRecent();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputId.trim();
    if (trimmed) setActiveId(trimmed);
  };

  return (
    <div className="border rounded-lg p-4 bg-card space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Conversation Trace
        </h3>
        <span className="text-xs text-muted-foreground">Debug a session by ID or pick a recent one</span>
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={inputId}
          onChange={(e) => setInputId(e.target.value)}
          placeholder="Paste a conversationId / threadId…"
          className="flex-1 border rounded px-3 py-1.5 text-sm bg-background font-mono"
        />
        <button
          type="submit"
          className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:opacity-90"
        >
          Load Trace
        </button>
      </form>

      {/* Recent conversations panel */}
      {!activeId && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Recent (last 24h, your user):</div>
          {recentLoading ? (
            <div className="text-xs text-muted-foreground py-2">Loading…</div>
          ) : recent.length === 0 ? (
            <div className="text-xs text-muted-foreground py-2">No recent conversations.</div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {recent.map((c) => (
                <button
                  key={c.conversationId}
                  onClick={() => setActiveId(c.conversationId)}
                  className="w-full flex items-center gap-3 py-1.5 px-2 rounded hover:bg-muted/40 text-xs text-left"
                >
                  <span className="font-mono truncate flex-1" title={c.conversationId}>
                    {c.conversationId}
                  </span>
                  <span className="tabular-nums text-muted-foreground w-12 text-right">
                    {c.callCount}×
                  </span>
                  <span className="tabular-nums w-16 text-right">{formatCost(c.totalCost)}</span>
                  <span className="text-muted-foreground w-32 text-right">
                    {new Date(c.lastSeen).toLocaleString()}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Active trace */}
      {activeId && (
        <div className="border-t pt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-mono text-muted-foreground truncate" title={activeId}>
              {activeId}
            </span>
            <button
              onClick={() => {
                setActiveId(null);
                setInputId('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          </div>
          <ConversationTrace conversationId={activeId} />
        </div>
      )}
    </div>
  );
};

const ConversationTrace: React.FC<{ conversationId: string }> = ({ conversationId }) => {
  const [records, setRecords] = useState<TraceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrace = async () => {
      try {
        const client = getAIUsageClient();
        const { data } = await client.get<{ success: boolean; data: TraceRecord[] }>(
          `/conversation/${conversationId}`
        );
        setRecords(data.data || []);
      } catch {
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTrace();
  }, [conversationId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 px-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading trace...</span>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="py-4 px-6 text-sm text-muted-foreground">No trace records found.</div>
    );
  }

  // Phase 6.8.2: Conversation summary header.
  const summary = records.reduce(
    (acc, r) => {
      acc.totalCalls += 1;
      acc.totalPromptTokens += r.tokens.prompt || 0;
      acc.totalCompletionTokens += r.tokens.completion || 0;
      acc.estimatedCost += r.cost || 0;
      acc.actualCost += r.actualCost || 0;
      if (r.aiModel) acc.models.add(r.aiModel);
      if (r.agentType) acc.agents.add(r.agentType);
      return acc;
    },
    {
      totalCalls: 0,
      totalPromptTokens: 0,
      totalCompletionTokens: 0,
      estimatedCost: 0,
      actualCost: 0,
      models: new Set<string>(),
      agents: new Set<string>(),
    },
  );
  const first = records[0]?.timestamp ? new Date(records[0].timestamp) : null;
  const last = records[records.length - 1]?.timestamp
    ? new Date(records[records.length - 1].timestamp)
    : null;
  const durationMs = first && last ? last.getTime() - first.getTime() : 0;

  return (
    <div className="px-6 py-3 space-y-2">
      {/* Phase 6.8.2: Summary header */}
      <div className="flex flex-wrap items-center gap-4 px-3 py-2 rounded-md bg-card border text-xs">
        <span className="font-semibold">{summary.totalCalls} calls</span>
        <span>
          <span className="text-muted-foreground">in</span> {formatTokens(summary.totalPromptTokens)}{' '}
          <span className="text-muted-foreground">·</span> out {formatTokens(summary.totalCompletionTokens)}
        </span>
        <span>
          <span className="text-muted-foreground">est</span> {formatCost(summary.estimatedCost)}
          {summary.actualCost > 0 && (
            <span className="ml-2 text-emerald-600">actual {formatCost(summary.actualCost)}</span>
          )}
        </span>
        <span className="text-muted-foreground">{formatMs(durationMs)}</span>
        <span className="text-muted-foreground truncate" title={Array.from(summary.models).join(', ')}>
          models: {Array.from(summary.models).join(', ') || '—'}
        </span>
        {summary.agents.size > 0 && (
          <span className="text-muted-foreground truncate">
            agents: {Array.from(summary.agents).join(', ')}
          </span>
        )}
      </div>
      {records.map((rec, idx) => {
        const generationLink =
          rec.generationId && (rec.vendor === 'openrouter' || rec.provider === 'openrouter')
            ? `https://openrouter.ai/activity?gen_id=${encodeURIComponent(rec.generationId)}`
            : null;
        return (
          <div
            key={rec._id}
            className="flex items-center gap-3 py-2 px-3 rounded-md bg-muted/30 text-xs"
          >
            <span className="text-muted-foreground w-5 text-right">{idx + 1}</span>
            <div className="w-px h-4 bg-border" />
            {rec.success ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
            )}
            <span className="font-medium min-w-[140px]">{rec.requestType}</span>
            <span className="text-muted-foreground min-w-[140px]">{rec.aiModel}</span>
            {rec.vendor && (
              <span className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:text-purple-400 font-medium text-[10px]">
                {rec.vendor}
              </span>
            )}
            <span className="text-muted-foreground min-w-[80px]">{rec.service || '—'}</span>
            {rec.agentType && (
              <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">
                {rec.agentType}
              </span>
            )}
            <span className="tabular-nums min-w-[80px] text-right" title="prompt / completion">
              {formatTokens(rec.tokens.prompt)} / {formatTokens(rec.tokens.completion)}
            </span>
            <span className="tabular-nums min-w-[70px] text-right">
              {formatCost(rec.cost)}
              {rec.costVerified && rec.actualCost ? (
                <span className="ml-1 text-emerald-600 text-[10px]">✓</span>
              ) : null}
            </span>
            <span className="tabular-nums min-w-[50px] text-right">{formatMs(rec.responseTime)}</span>
            {rec.cached && (
              <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">
                cached
              </span>
            )}
            {generationLink && (
              <a
                href={generationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
                title="View on OpenRouter"
              >
                ↗
              </a>
            )}
            <span className="text-muted-foreground ml-auto">
              {new Date(rec.timestamp).toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Maps a groupBy field name in a CostGroup row to the /history filter
// query-param name. Used by row drill-down to fetch the underlying records.
const GROUPBY_TO_HISTORY_PARAM: Record<string, string> = {
  service: 'service',
  model: 'aiModel',
  user: 'userId',
  provider: 'provider',
  vendor: 'vendor',
  requestType: 'requestType',
  organization: 'organizationId',
  conversation: 'conversationId',
  agentType: 'agentType',
};

function buildHistoryFiltersFromRow(
  row: CostGroup,
  groupByFields: string[],
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const field of groupByFields) {
    const val = row.groupBy?.[field];
    if (val == null) continue;
    const param = GROUPBY_TO_HISTORY_PARAM[field];
    if (param) {
      params[param] = String(val);
    }
  }
  return params;
}

interface HistoryRecord {
  _id: string;
  timestamp: string;
  service: string | null;
  vendor?: string | null;
  provider: string;
  aiModel: string;
  requestType: string;
  agentType: string | null;
  tokens: { prompt: number; completion: number; total: number };
  cost: number;
  actualCost?: number | null;
  costVerified?: boolean;
  cached: boolean;
  responseTime: number;
  success: boolean;
  generationId?: string | null;
  conversationId?: string | null;
}

const RecordRow: React.FC<{ rec: HistoryRecord; idx: number }> = ({ rec, idx }) => {
  const generationLink =
    rec.generationId && (rec.vendor === 'openrouter' || rec.provider === 'openrouter')
      ? `https://openrouter.ai/activity?gen_id=${encodeURIComponent(rec.generationId)}`
      : null;
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-md bg-muted/30 text-xs">
      <span className="text-muted-foreground w-5 text-right">{idx + 1}</span>
      <div className="w-px h-4 bg-border" />
      {rec.success ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
      ) : (
        <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
      )}
      <span className="font-medium min-w-[140px]">{rec.requestType}</span>
      <span className="text-muted-foreground min-w-[140px]">{rec.aiModel}</span>
      {rec.vendor && (
        <span className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:text-purple-400 font-medium text-[10px]">
          {rec.vendor}
        </span>
      )}
      <span className="text-muted-foreground min-w-[80px]">{rec.service || '—'}</span>
      {rec.agentType && (
        <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400 font-medium">
          {rec.agentType}
        </span>
      )}
      <span className="tabular-nums min-w-[80px] text-right" title="prompt / completion">
        {formatTokens(rec.tokens.prompt)} / {formatTokens(rec.tokens.completion)}
      </span>
      <span className="tabular-nums min-w-[70px] text-right">
        {formatCost(rec.cost)}
        {rec.costVerified && rec.actualCost ? (
          <span className="ml-1 text-emerald-600 text-[10px]">✓</span>
        ) : null}
      </span>
      <span className="tabular-nums min-w-[50px] text-right">{formatMs(rec.responseTime)}</span>
      {rec.cached && (
        <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">
          cached
        </span>
      )}
      {generationLink && (
        <a
          href={generationLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline"
          title="View on OpenRouter"
        >
          ↗
        </a>
      )}
      <span className="text-muted-foreground ml-auto">
        {new Date(rec.timestamp).toLocaleString()}
      </span>
    </div>
  );
};

const GroupRecordsList: React.FC<{
  filters: Record<string, string>;
  windowMinutes: number;
}> = ({ filters, windowMinutes }) => {
  const [records, setRecords] = useState<HistoryRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const filtersQuery = serializeHistoryFilters(filters);

  useEffect(() => {
    let cancelled = false;
    const fetchRecords = async () => {
      setLoading(true);
      setError(null);
      try {
        const requestFilters = Object.fromEntries(new URLSearchParams(filtersQuery).entries());
        const client = getAIUsageClient();
        const { data } = await client.get<{
          success: boolean;
          data: HistoryRecord[];
          meta: { pagination: { totalRecords: number } };
        }>('/history', {
          params: { ...requestFilters, minutes: windowMinutes, limit: 100 },
        });
        if (!cancelled) {
          setRecords(data.data || []);
          setTotal(data.meta?.pagination?.totalRecords ?? data.data?.length ?? 0);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load records');
          setRecords([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRecords();
    return () => {
      cancelled = true;
    };
  }, [filtersQuery, windowMinutes]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 px-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading records…</span>
      </div>
    );
  }
  if (error) {
    return <div className="py-4 px-6 text-sm text-red-600">{error}</div>;
  }
  if (records.length === 0) {
    return <div className="py-4 px-6 text-sm text-muted-foreground">No records found.</div>;
  }

  return (
    <div className="px-6 py-3 space-y-1">
      <div className="text-xs text-muted-foreground px-3 pb-1">
        Showing {records.length}{total > records.length ? ` of ${total}` : ''} records
        {Object.keys(filters).length > 0 && (
          <span className="ml-2">
            · filters: {Object.entries(filters).map(([k, v]) => `${k}=${v}`).join(', ')}
          </span>
        )}
      </div>
      {records.map((rec, idx) => (
        <RecordRow key={rec._id} rec={rec} idx={idx} />
      ))}
    </div>
  );
};

const CostTable: React.FC<{
  data: CostGroup[];
  groupByFields: string[];
  loading: boolean;
  isConversationView?: boolean;
  windowMinutes: number;
}> = ({ data, groupByFields, loading, isConversationView, windowMinutes }) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <BarChart3 className="h-10 w-10 mb-2 opacity-40" />
        <div className="text-sm">No usage data found for this period</div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="w-8 py-3 px-2" />
            {groupByFields.map((field) => (
              <th key={field} className="text-left py-3 px-3 font-medium text-muted-foreground capitalize">
                {field}
              </th>
            ))}
            <th className="text-right py-3 px-3 font-medium text-muted-foreground">Requests</th>
            <th className="text-right py-3 px-3 font-medium text-muted-foreground">Tokens</th>
            <th className="text-right py-3 px-3 font-medium text-muted-foreground">Est. Cost</th>
            <th className="text-right py-3 px-3 font-medium text-muted-foreground">Actual Cost</th>
            <th className="text-right py-3 px-3 font-medium text-muted-foreground">Verified</th>
            <th className="text-right py-3 px-3 font-medium text-muted-foreground">Avg Time</th>
            <th className="text-right py-3 px-3 font-medium text-muted-foreground">Success</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => {
            const isExpanded = expandedRows.has(i);
            const convId = row.groupBy?.conversation;
            // Derive a stable key from the groupBy values so reordering or
            // group-by changes don't cause React to reconcile the wrong row.
            const rowKey = groupByFields
              .map((f) => String(row.groupBy?.[f] ?? '∅'))
              .join('|') || `row-${i}`;
            return (
              <React.Fragment key={rowKey}>
                <tr
                  className={`border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer`}
                  onClick={() => toggleRow(i)}
                >
                  <td className="py-3 px-2 text-muted-foreground">
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </td>
                  {groupByFields.map((field) => {
                    const val = row.groupBy?.[field];
                    const displayVal = field === 'user' && row.groupBy?.username
                      ? row.groupBy.username
                      : field === 'organization' && row.groupBy?.organizationName
                      ? row.groupBy.organizationName
                      : groupByLabel(field, val);
                    return (
                      <td key={field} className="py-3 px-3 font-medium truncate max-w-[200px]" title={displayVal}>
                        {displayVal}
                      </td>
                    );
                  })}
                  <td className="text-right py-3 px-3 tabular-nums">{row.totalRequests.toLocaleString()}</td>
                  <td className="text-right py-3 px-3 tabular-nums">{formatTokens(row.totalTokens)}</td>
                  <td className="text-right py-3 px-3 tabular-nums font-medium">{formatCost(row.estimatedCost)}</td>
                  <td className="text-right py-3 px-3 tabular-nums font-medium">
                    {row.actualCost > 0 ? (
                      <span className="text-green-600 dark:text-green-400">{formatCost(row.actualCost)}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="text-right py-3 px-3 tabular-nums">
                    {row.verifiedCount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                        <CheckCircle2 className="h-3 w-3" />
                        {row.verifiedCount}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                  <td className="text-right py-3 px-3 tabular-nums">{formatMs(row.avgResponseTime)}</td>
                  <td className="text-right py-3 px-3 tabular-nums">
                    <span className={row.successRate >= 95 ? 'text-green-600 dark:text-green-400' : row.successRate >= 80 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}>
                      {row.successRate}%
                    </span>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={groupByFields.length + 8} className="p-0 bg-muted/20 border-b border-border/50">
                      {isConversationView && convId ? (
                        <ConversationTrace conversationId={String(convId)} />
                      ) : (
                        <GroupRecordsList
                          filters={buildHistoryFiltersFromRow(row, groupByFields)}
                          windowMinutes={windowMinutes}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const AdminAIUsageTab: React.FC = () => {
  const [groupBy, setGroupBy] = useState<GroupByOption>('service');
  const [windowMinutes, setWindowMinutes] = useState(30 * 24 * 60); // default 30 days
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [costData, setCostData] = useState<CostGroup[]>([]);
  const [costMeta, setCostMeta] = useState<CostResponse['meta'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dashboard summary
  const [summary, setSummary] = useState<DashboardResponse['data']['summary'] | null>(null);
  const [_summaryLoading, setSummaryLoading] = useState(false);

  // OpenRouter balance
  const [balance, setBalance] = useState<OpenRouterBalance | null>(null);

  // Cost verification
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = getAIUsageClient();
      const { data } = await client.get<CostResponse>('/costs', {
        params: { groupBy, minutes: windowMinutes, verifiedOnly },
      });
      setCostData(data.data);
      setCostMeta(data.meta);
    } catch (err: unknown) {
      setError(errorUtils.getApiErrorMessage(err, 'Failed to fetch cost data'));
      setCostData([]);
    } finally {
      setLoading(false);
    }
  }, [groupBy, windowMinutes, verifiedOnly]);

  const fetchDashboard = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const client = getAIUsageClient();
      const { data } = await client.get<DashboardResponse>('/dashboard');
      setSummary(data.data?.summary || null);
    } catch {
      // Dashboard is supplementary, don't block on errors
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchBalance = useCallback(async () => {
    try {
      const client = getAIUsageClient();
      const { data } = await client.get<{ success: boolean; data: OpenRouterBalance }>('/openrouter-balance');
      setBalance(data.data);
    } catch {
      // Balance is supplementary
    }
  }, []);

  const triggerVerification = useCallback(async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const client = getAIUsageClient();
      // body must be {} not null — express body-parser strict mode rejects
      // a top-level `null` body and returns 400 before any middleware runs.
      const { data } = await client.post('/verify-costs', {}, {
        params: { batchSize: 100 },
      });
      setVerifyResult(data.data);
      // Refresh costs after verification
      fetchCosts();
    } catch (err: unknown) {
      // Surface concrete diagnostic info — generic "Something went wrong"
      // hides which layer actually rejected the request.
      const e = isAxiosLikeError(err) ? err : undefined;
      let detail: string;
      if (e?.response) {
        const status = e.response.status ?? '?';
        const code = e.response.data?.code;
        const message =
          e.response.data?.error || e.response.data?.message || JSON.stringify(e.response.data);
        detail = `HTTP ${status}${code ? ` [${code}]` : ''} — ${message}`;
      } else if (e?.request) {
        detail = `No response from server (network / proxy): ${e.message ?? 'unknown'}`;
      } else {
        detail = e?.message ?? 'Unknown error';
      }
      console.error('[VerifyCosts] failure', err);
      setError(`Verification failed — ${detail}`);
    } finally {
      setVerifying(false);
    }
  }, [fetchCosts]);

  useEffect(() => {
    fetchCosts();
    fetchDashboard();
    fetchBalance();
  }, [fetchCosts, fetchDashboard, fetchBalance]);

  // Compute totals from cost data
  const totals = costData.reduce(
    (acc, row) => ({
      requests: acc.requests + row.totalRequests,
      tokens: acc.tokens + row.totalTokens,
      estimatedCost: acc.estimatedCost + row.estimatedCost,
      actualCost: acc.actualCost + row.actualCost,
      verified: acc.verified + row.verifiedCount,
    }),
    { requests: 0, tokens: 0, estimatedCost: 0, actualCost: 0, verified: 0 }
  );

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Conversation Lookup (Phase 6.8.1 + 6.8.4) — debug surface for UserAgent etc. */}
      <ConversationLookup />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <SummaryCard
          label="Total Requests"
          value={totals.requests.toLocaleString()}
          subtitle={`Last ${periodLabel(windowMinutes)}`}
          icon={<Activity className="h-5 w-5" />}
          color="text-blue-500"
        />
        <SummaryCard
          label="Total Tokens"
          value={formatTokens(totals.tokens)}
          subtitle={`${totals.tokens.toLocaleString()} total`}
          icon={<Zap className="h-5 w-5" />}
          color="text-purple-500"
        />
        <SummaryCard
          label="Estimated Cost"
          value={formatCost(totals.estimatedCost)}
          subtitle="Local estimates"
          icon={<DollarSign className="h-5 w-5" />}
          color="text-amber-500"
        />
        <SummaryCard
          label="Actual Cost"
          value={formatCost(totals.actualCost)}
          subtitle="OpenRouter verified"
          icon={<DollarSign className="h-5 w-5" />}
          color="text-green-500"
        />
        <SummaryCard
          label="Verified"
          value={totals.verified.toLocaleString()}
          subtitle={`of ${totals.requests.toLocaleString()} requests`}
          icon={<CheckCircle2 className="h-5 w-5" />}
          color="text-emerald-500"
        />
        <SummaryCard
          label="Avg Response"
          value={summary ? formatMs(summary.avgResponseTime) : '—'}
          subtitle={summary?.successRate != null ? `${summary.successRate.toFixed(1)}% success` : ''}
          icon={<Clock className="h-5 w-5" />}
          color="text-cyan-500"
        />
        <SummaryCard
          label="Daily Limit"
          value={balance ? `${formatCost(balance.usageDaily)} / ${formatCost(balance.limit ?? 0)}` : '—'}
          subtitle={balance ? `${formatCost(balance.limitRemaining ?? 0)} remaining today` : 'Loading...'}
          icon={<TrendingUp className="h-5 w-5" />}
          color="text-orange-500"
        />
        <SummaryCard
          label="Account Balance"
          value={balance?.accountBalance != null ? formatCost(balance.accountBalance) : '—'}
          subtitle={balance?.totalCredits != null ? `${formatCost(balance.totalCredits)} purchased · ${formatCost(balance.totalUsage ?? 0)} used` : 'Loading...'}
          icon={<Wallet className="h-5 w-5" />}
          color="text-emerald-600"
        />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 border rounded-lg p-4 bg-card">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Group by:</span>
          <div className="flex flex-wrap gap-1">
            {GROUP_BY_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={groupBy === opt.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setGroupBy(opt.value)}
                className="gap-1.5"
              >
                {opt.icon}
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="h-6 w-px bg-border mx-1" />

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Period:</span>
          <select
            value={windowMinutes}
            onChange={(e) => setWindowMinutes(Number(e.target.value))}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
          >
            {PERIOD_OPTIONS.map((p) => (
              <option key={p.minutes} value={p.minutes}>{p.label}</option>
            ))}
          </select>
        </div>

        <div className="h-6 w-px bg-border mx-1" />

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={verifiedOnly}
            onChange={(e) => setVerifiedOnly(e.target.checked)}
            className="rounded border-input"
          />
          Verified only
        </label>

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          onClick={triggerVerification}
          disabled={verifying}
          className="gap-1.5"
        >
          {verifying ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <TrendingUp className="h-4 w-4" />
          )}
          {verifying ? 'Verifying...' : 'Verify Costs'}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={fetchCosts}
          disabled={loading}
          className="gap-1.5"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Verification Result */}
      {verifyResult && (
        <div className="flex items-center gap-3 border rounded-lg p-3 bg-green-500/5 border-green-500/20">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <div className="text-sm">
            <span className="font-medium">Verification complete:</span>{' '}
            {verifyResult.processed} processed, {verifyResult.verified} verified, {verifyResult.failed} failed
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 border rounded-lg p-3 bg-red-500/5 border-red-500/20">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
        </div>
      )}

      {/* Cost Table */}
      <div className="border rounded-lg bg-card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-medium text-sm">
            Cost Breakdown by {GROUP_BY_OPTIONS.find(o => o.value === groupBy)?.label}
          </h3>
          {costMeta && (
            <span className="text-xs text-muted-foreground">
              {costMeta.totalGroups} groups &middot; Last {periodLabel(costMeta.windowMinutes ?? costMeta.days * 24 * 60)}
            </span>
          )}
        </div>
        <CostTable data={costData} groupByFields={costMeta?.groupBy || [groupBy]} loading={loading} isConversationView={groupBy === 'conversation'} windowMinutes={windowMinutes} />
      </div>
    </div>
  );
};
